import {
	DELIVERY_METHOD_KIND,
	type DeliveryMethodKind,
} from "@dransay/api/models";
import { useMemo, useState } from "react";
import {
	isExpressCourierAvailable,
	isUberDirectExpress,
	type VendorsFilteredItem,
} from "./vendors-filtered.contract";

type UseMapExpressCourierFilterParams = {
	enablePharmacyFilters: boolean;
	deliveryMethodKind?: DeliveryMethodKind | null;
	postalCode: string;
	/** The fetched getVendorsFiltered page, already carrying express verdicts. */
	vendors: VendorsFilteredItem[];
	expressCheckFailed: boolean;
	selected?: boolean;
	setSelected?: (selected: boolean) => void;
};

type UseMapExpressCourierFilterResult = {
	/** Whether the chip has an entry point on this surface at all. */
	showFilter: boolean;
	selected: boolean;
	setSelected: (selected: boolean) => void;
	/** The page narrowed to express-reachable pharmacies while the chip is on. */
	vendors: VendorsFilteredItem[];
	/** The chip is on but the availability check could not be completed. */
	checkFailed: boolean;
};

/**
 * Express courier is a live per-pharmacy verdict rather than a row field, so the
 * chip filters the page the list already fetched instead of issuing its own
 * request: a courier list asks the server for `["courier","express-courier"]`
 * whenever an address is known, so every row arrives with
 * `expressCourierAvailable` already resolved. That flag is the Lieferando
 * zone. The vendor query only proves the aggregation flag and the 10 km
 * radius, so a Lieferando pharmacy inside 10 km can still be outside its
 * zone. Uber rows never receive a Lieferando verdict — the query's window
 * and radius are theirs — so the filter keeps them. Filtering here, above
 * the marker mapping, keeps map markers and list rows on one set.
 *
 * Lives in a hook of its own so VendorFilterMapWrapper stays inside the
 * cognitive-complexity budget.
 */
export function useMapExpressCourierFilter({
	enablePharmacyFilters,
	deliveryMethodKind,
	postalCode,
	vendors,
	expressCheckFailed,
	selected: selectedProp,
	setSelected: setSelectedProp,
}: UseMapExpressCourierFilterParams): UseMapExpressCourierFilterResult {
	const [selectedState, setSelectedState] = useState(false);
	const selected = selectedProp ?? selectedState;
	const setSelected = setSelectedProp ?? setSelectedState;
	const showFilter =
		enablePharmacyFilters &&
		deliveryMethodKind === DELIVERY_METHOD_KIND.COURIER &&
		postalCode !== "";
	// Derived rather than reset through an effect: leaving courier (or losing
	// the address) hides the chip, and a hidden filter must stop filtering.
	const active = showFilter && selected;
	const filteredVendors = useMemo(
		() =>
			active
				? vendors.filter(
						(vendor) =>
							isUberDirectExpress(vendor) || isExpressCourierAvailable(vendor),
					)
				: vendors,
		[active, vendors],
	);
	return {
		showFilter,
		selected,
		setSelected,
		vendors: filteredVendors,
		checkFailed: active && expressCheckFailed,
	};
}
